import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const { token, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && token) {
      const socketInstance = io(
        process.env.REACT_APP_SERVER_URL || 'http://localhost:5001',
        {
          auth: { token },
          transports: ['websocket', 'polling']
        }
      );

      socketInstance.on('connect', () => {
        setConnected(true);
        console.log('Socket connected');
      });

      socketInstance.on('disconnect', () => {
        setConnected(false);
        setActiveUsers([]);
        console.log('Socket disconnected');
      });

      socketInstance.on('connect_error', (error) => {
        console.error('Socket connection error:', error.message);
      });

      // El servidor ens ha desplaçat: el grup ha entrat amb el codi en un altre
      // dispositiu, o l'instructor li ha revocat el codi o tancat la sessió.
      // Sense això, aquesta pantalla es quedaria amb el websocket tancat però
      // ensenyant l'exercici fins que algú premés alguna cosa.
      socketInstance.on('session_displaced', ({ message }) => {
        localStorage.removeItem('token');
        window.alert(message || 'La sessió s\'ha obert en un altre dispositiu.');
        window.location.href = '/login';
      });

      // Handle user presence
      socketInstance.on('user_joined', (user) => {
        setActiveUsers(prev => {
          if (prev.find(u => u.userId === user.userId)) return prev;
          return [...prev, user];
        });
      });

      socketInstance.on('user_left', ({ userId }) => {
        setActiveUsers(prev => prev.filter(u => u.userId !== userId));
      });

      setSocket(socketInstance);

      return () => {
        socketInstance.disconnect();
        setSocket(null);
        setConnected(false);
      };
    }
  }, [isAuthenticated, token]);

  // Join an assessment room for collaboration
  const joinAssessment = useCallback((assessmentId) => {
    if (socket) {
      socket.emit('join_assessment', assessmentId);
    }
  }, [socket]);

  // Leave an assessment room
  const leaveAssessment = useCallback((assessmentId) => {
    if (socket) {
      socket.emit('leave_assessment', assessmentId);
      setActiveUsers([]);
    }
  }, [socket]);

  // Emit item created event
  const emitItemCreated = useCallback((assessmentId, item) => {
    if (socket) {
      socket.emit('item_created', { assessmentId, item });
    }
  }, [socket]);

  // Emit item updated event
  const emitItemUpdated = useCallback((assessmentId, item) => {
    if (socket) {
      socket.emit('item_updated', { assessmentId, item });
    }
  }, [socket]);

  // Emit item deleted event
  const emitItemDeleted = useCallback((assessmentId, itemId) => {
    if (socket) {
      socket.emit('item_deleted', { assessmentId, itemId });
    }
  }, [socket]);

  // Send chat message
  const sendChatMessage = useCallback((assessmentId, message) => {
    if (socket) {
      socket.emit('chat_message', { assessmentId, message });
    }
  }, [socket]);

  // Subscribe to events
  const on = useCallback((event, callback) => {
    if (socket) {
      socket.on(event, callback);
      return () => socket.off(event, callback);
    }
    return () => {};
  }, [socket]);

  // Unsubscribe from events
  const off = useCallback((event, callback) => {
    if (socket) {
      socket.off(event, callback);
    }
  }, [socket]);

  const value = {
    socket,
    connected,
    activeUsers,
    joinAssessment,
    leaveAssessment,
    emitItemCreated,
    emitItemUpdated,
    emitItemDeleted,
    sendChatMessage,
    on,
    off
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
